import { AlertColor, Autocomplete, Box, Dialog, DialogContent, DialogTitle, TextField } from "@mui/material"
import { ButtonStyled, PrimaryButton } from "../../../../../../theme/buttons";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Consecutive } from "../../../../../../interfaces/Commons/consecutives";
import { Resource } from "../../../../../../interfaces/Security/resources";
import { FormsErrors } from "../../../../../../hooks/Forms/FormsErrors";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import GenericPromises from "../../../../../../api/GenericPromises";
import { Spinner } from "../../../../../../components/Commons/Spinner/Spinner";

interface DialogProps {
    open: boolean,
    setOpen: (open: boolean) => void,
    comboResources: Resource[],
    onPut: (data: Consecutive) => void,
    defaultConsecutive: Consecutive,
    setConsecutivePayload: Dispatch<SetStateAction<Consecutive>>
}

export const DialogUpdateConsecutive = ({ open, setOpen, comboResources, onPut, defaultConsecutive, setConsecutivePayload }: DialogProps) => {
    const [t] = useTranslation("global");
    const { GetError } = FormsErrors();
    const [defaultResource, setDefaultResource] = useState<Resource>();
    const [isLoadingData, setIsLoadingData] = useState(false);

    const {
        trigger,
        control,
        getValues,
        reset,
        formState: { errors },
    } = useForm<Consecutive>({
        defaultValues: {
            name: "",
            description: "",
            prefix: "",
            length: undefined,
            consecutive: undefined,
        },
    });

    const closeDialog = () => {
        setIsLoadingData(false);
        setOpen(false);
        setConsecutivePayload({});
    }

    useEffect(() => {
        const getData = async () => {
            await reset({
                name: defaultConsecutive.name ?? undefined,
                description: defaultConsecutive.description ?? undefined,
                prefix: defaultConsecutive.prefix ?? undefined,
                length: defaultConsecutive.length ?? undefined,
                consecutive: defaultConsecutive.consecutive ?? undefined,
                resource_id: defaultConsecutive.resource_id ?? undefined,
                company_location_id: defaultConsecutive.company_location_id ?? undefined,
            });
            setDefaultResource(comboResources.find((item) => item.resource_id === defaultConsecutive.resource_id));
            setIsLoadingData(true);
        }

        if (defaultConsecutive && defaultConsecutive.name) {
            getData();
        }
    }, [defaultConsecutive])


    return (
        <>
            <Dialog
                open={open}
                aria-labelledby="responsive-dialog-title"
                fullWidth
                maxWidth={'lg'}
                disableEscapeKeyDown={true}
            >
                <DialogTitle id="responsive-dialog-title">
                    {t("consecutives.title-view")}
                </DialogTitle>
                <DialogContent>
                    <div className="d-flex justify-content-center">
                        <div className="custom-card-view w-75">
                            <form className="mt-4">
                                <div className="custom-card-row d-flex justify-content-between">
                                    <div className="w-100">
                                        {!isLoadingData ?
                                            <Spinner isBox={false} /> : <>
                                                <div className="d-flex w-100">
                                                    <div className="w-25">
                                                        <Controller
                                                            name="name"
                                                            control={control}
                                                            rules={{ required: true, maxLength: 30 }}
                                                            render={({ field }) => (
                                                                <TextField
                                                                    variant="filled"
                                                                    label={`${t("consecutives.fields.name")}`}
                                                                    ref={field.ref}
                                                                    value={field.value}
                                                                    onChange={(event) => {
                                                                        field.onChange(event.target.value);
                                                                    }}
                                                                    error={!!errors.name}
                                                                    helperText={GetError(errors.name?.type)}
                                                                    size="small"
                                                                    style={{ width: "100%" }}
                                                                    sx={{ paddingRight: 2 }}
                                                                />
                                                            )}
                                                        />
                                                    </div>

                                                    <div className="w-25">
                                                        <Controller
                                                            name="description"
                                                            control={control}
                                                            rules={{ required: true, maxLength: 30 }}
                                                            render={({ field }) => (
                                                                <TextField
                                                                    variant="filled"
                                                                    label={`${t("consecutives.fields.description")}`}
                                                                    ref={field.ref}
                                                                    value={field.value}
                                                                    onChange={(event) => {
                                                                        field.onChange(event.target.value);
                                                                    }}
                                                                    error={!!errors.description}
                                                                    helperText={GetError(errors.description?.type)}
                                                                    size="small"
                                                                    style={{ width: "100%" }}
                                                                    sx={{ paddingRight: 2 }}
                                                                />
                                                            )}
                                                        />
                                                    </div>

                                                    <div className="w-25">
                                                        <Controller
                                                            name="resource_id"
                                                            control={control}
                                                            rules={{ required: true }}
                                                            render={({ field }) => (
                                                                <Autocomplete
                                                                    ref={field.ref}
                                                                    size="small"
                                                                    sx={{ width: "100%" }}
                                                                    options={comboResources}
                                                                    defaultValue={defaultResource}
                                                                    getOptionLabel={(option) => option.resource_name ?? ""}
                                                                    renderOption={(props, option: Resource) => (
                                                                        <div key={option.resource_id}>
                                                                            <Box
                                                                                component="li"
                                                                                sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                                                                                {...props}
                                                                            >
                                                                                {option.resource_name}
                                                                            </Box>
                                                                        </div>
                                                                    )}
                                                                    isOptionEqualToValue={(option, value) =>
                                                                        option.resource_id === value.resource_id
                                                                    }
                                                                    onChange={(_, values) => {
                                                                        field.onChange(values?.resource_id || null);
                                                                    }}
                                                                    renderInput={(params) => (
                                                                        <TextField
                                                                            {...params}
                                                                            variant="filled"
                                                                            label={t("consecutives.fields.resource_id")}
                                                                            value={field.value}
                                                                            error={
                                                                                !!errors.resource_id || field.value === null
                                                                            }
                                                                            helperText={
                                                                                errors.resource_id?.type ||
                                                                                    field.value === null
                                                                                    ? t("generic.forms-errors.required")
                                                                                    : null
                                                                            }
                                                                            sx={{ paddingRight: 2 }}
                                                                        />
                                                                    )}
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                    <div className="w-25">
                                                        <Controller
                                                            name="prefix"
                                                            control={control}
                                                            rules={{ required: true, maxLength: 30 }}
                                                            render={({ field }) => (
                                                                <TextField
                                                                    variant="filled"
                                                                    label={`${t("consecutives.fields.prefix")}`}
                                                                    ref={field.ref}
                                                                    value={field.value}
                                                                    onChange={(event) => {
                                                                        field.onChange(event.target.value);
                                                                    }}
                                                                    error={!!errors.prefix}
                                                                    helperText={GetError(errors.prefix?.type)}
                                                                    size="small"
                                                                    style={{ width: "100%" }}
                                                                    sx={{ paddingRight: 2 }}
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="d-flex pt-3">
                                                    <div className="w-50">
                                                        <Controller
                                                            name="length"
                                                            control={control}
                                                            rules={{ required: true, maxLength: 30 }}
                                                            render={({ field }) => (
                                                                <TextField
                                                                    variant="filled"
                                                                    label={`${t("consecutives.fields.length")}`}
                                                                    ref={field.ref}
                                                                    value={field.value}
                                                                    onChange={(event) => {
                                                                        field.onChange(event.target.value);
                                                                    }}
                                                                    error={!!errors.prefix}
                                                                    helperText={GetError(errors.prefix?.type)}
                                                                    size="small"
                                                                    style={{ width: "100%" }}
                                                                    sx={{
                                                                        paddingRight: 2,
                                                                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                                                                        "& input[type=number]": { MozAppearance: "textfield", }
                                                                    }}
                                                                    type="number"
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                    <div className="w-50">
                                                        <Controller
                                                            name="consecutive"
                                                            control={control}
                                                            rules={{ required: true }}
                                                            render={({ field }) => (
                                                                <TextField
                                                                    variant="filled"
                                                                    label={`${t("consecutives.fields.consecutive")}`}
                                                                    ref={field.ref}
                                                                    value={field.value}
                                                                    onChange={(event) => {
                                                                        field.onChange(event.target.value);
                                                                    }}
                                                                    error={!!errors.consecutive}
                                                                    helperText={GetError(errors.consecutive?.type)}
                                                                    size="small"
                                                                    style={{ width: "100%" }}
                                                                    sx={{
                                                                        paddingRight: 2,
                                                                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                                                                        "& input[type=number]": { MozAppearance: "textfield", }
                                                                    }}
                                                                    type="number"
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                    <div className="w-50"></div>
                                                    <div className="w-50"></div>
                                                </div>
                                            </>}
                                    </div>
                                </div>

                                <div className="d-flex flex-row-reverse mt-1">
                                    <ButtonStyled
                                        variant="outlined"
                                        className="m-1"
                                        onClick={async () => {
                                            trigger().then(async (result) => {
                                                if (result) {
                                                    onPut(getValues());
                                                    closeDialog();
                                                }
                                            })
                                        }
                                        }
                                    >
                                        {t("generic.buttons.accept")}
                                    </ButtonStyled>
                                    <ButtonStyled
                                        variant="contained"
                                        className="m-1"
                                        onClick={() => { setOpen(false); closeDialog(); }}
                                    >
                                        {t("generic.buttons.cancel")}
                                    </ButtonStyled>
                                </div>
                            </form>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    )
}
